/**
 * =============================================================================
 * FOCO CLINICO: AVISAR, NO RECHAZAR
 * =============================================================================
 * El campo "Foco clínico" del asistente de salas es un `Select mode="tags"`
 * sin opciones y sin validacion: se escribe a mano lo que sea. Medido sobre
 * produccion el 2026-08-09 sobre 214 salas: **67 etiquetas huerfanas** que no
 * existen en ninguna categoria de terapia. Entre ellas la misma palabra escrita
 * de tres formas y codigos internos de proyecto.
 *
 * Este modulo no cierra el campo. La decision es que el panel **avise**:
 *
 *   - Lo que solo difiere en tildes o mayusculas se arregla solo al guardar.
 *     "atencion" y "Atención" son la misma etiqueta; hacer que un admin las
 *     distinga a ojo es pedirle que haga de corrector.
 *   - Lo que nadie gobierna se guarda igual, pero se avisa de que no va a
 *     agrupar con ninguna categoria. Un admin es una persona y responde de lo
 *     que escribe; bloquearlo obligaria a desplegar cada vez que aparece un
 *     concepto clinico nuevo.
 *
 * =============================================================================
 * POR QUE AQUI NO HAY NINGUNA LISTA DE ETIQUETAS
 * =============================================================================
 * El vocabulario sale de las CATEGORIAS que el asistente ya tiene cargadas
 * (`therapyCats[].clinicalFocus`), que es la misma coleccion contra la que
 * valida el servidor.
 *
 * Copiar aqui las 18 etiquetas seria mas comodo y estaria mal: el dia que se de
 * de alta una categoria nueva, el panel marcaria su foco como huerfano hasta
 * que alguien se acordara de desplegar el frontend. Con esta forma, entra solo.
 *
 * Hay un test en el servidor —`focoClinico.portal.test.js`— que lee este
 * fichero y falla si vuelve a aparecer una lista escrita a mano.
 * =============================================================================
 */

/**
 * Clave de comparacion: sin tildes, sin mayusculas, sin espacios de sobra.
 * Es la misma que usa `server/utils/revi/clinicalVocabulary.js`.
 *
 * El rango de diacriticos va escrito por su codigo, no con los caracteres
 * literales. En este proyecto el encoding se rompe a diario, y
 * un rango de combinantes literal es justo lo que se ve mal y alguien
 * "arregla": escrito asi, no hay nada que arreglar.
 */
export function claveDeEtiqueta(texto) {
  return String(texto ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

/**
 * Indice `clave -> { etiqueta, categorias }` a partir de las categorias vivas.
 * Una misma etiqueta puede pertenecer a varias categorias.
 */
export function indiceDeFoco(therapyCats) {
  const indice = new Map();

  for (const categoria of therapyCats || []) {
    for (const bruta of categoria?.clinicalFocus || []) {
      const etiqueta = String(bruta ?? "").trim();
      if (!etiqueta) continue;

      const clave = claveDeEtiqueta(etiqueta);
      const previa = indice.get(clave);

      if (previa) {
        if (categoria?.title && !previa.categorias.includes(categoria.title)) {
          previa.categorias.push(categoria.title);
        }
      } else {
        indice.set(clave, {
          etiqueta,
          categorias: categoria?.title ? [categoria.title] : [],
        });
      }
    }
  }

  return indice;
}

/**
 * Opciones agrupadas por categoria para el desplegable.
 *
 * Se agrupa a proposito: ensenar "Memoria" suelta no dice nada, y ensenarla
 * bajo "Estimulación Cognitiva" explica por que existe. Una etiqueta que
 * pertenece a dos categorias aparece bajo las dos; antd deduplica por `value`.
 */
export function opcionesDeFoco(therapyCats) {
  return (therapyCats || [])
    .map((categoria) => ({
      label: categoria?.title || "Sin categoría",
      options: (categoria?.clinicalFocus || [])
        .map((bruta) => String(bruta ?? "").trim())
        .filter(Boolean)
        .map((etiqueta) => ({ label: etiqueta, value: etiqueta })),
    }))
    .filter((grupo) => grupo.options.length > 0);
}

/**
 * Revisa lo que hay escrito en el campo contra el vocabulario vivo.
 *
 * @param {string[]} seleccionadas Lo que tiene el formulario ahora mismo.
 * @param {object[]} therapyCats Categorias de terapia ya cargadas.
 * @returns {{
 *   valores: string[],
 *   corregidas: Array<{ de: string, a: string }>,
 *   huerfanas: string[],
 *   gobernadas: string[],
 * }}
 *   - `valores`: lo que hay que guardar, ya normalizado y sin repetidos.
 *   - `corregidas`: las que solo diferian en tildes o mayusculas.
 *   - `huerfanas`: las que no gobierna ninguna categoria, tal cual se escribieron.
 */
export function revisarFocoClinico(seleccionadas, therapyCats) {
  const indice = indiceDeFoco(therapyCats);

  const valores = [];
  const corregidas = [];
  const huerfanas = [];
  const gobernadas = [];
  const vistas = new Set();

  for (const bruta of seleccionadas || []) {
    const escrita = String(bruta ?? "").trim();
    if (!escrita) continue;

    const clave = claveDeEtiqueta(escrita);
    if (vistas.has(clave)) continue;
    vistas.add(clave);

    const conocida = indice.get(clave);

    if (!conocida) {
      // Se conserva TAL CUAL se escribio: es lo que hay que ensenar para que
      // quien la puso la reconozca.
      huerfanas.push(escrita);
      valores.push(escrita);
      continue;
    }

    if (conocida.etiqueta !== escrita) {
      corregidas.push({ de: escrita, a: conocida.etiqueta });
    }
    gobernadas.push(conocida.etiqueta);
    valores.push(conocida.etiqueta);
  }

  return { valores, corregidas, huerfanas, gobernadas };
}

/**
 * Lo que se manda al servidor: los valores ya normalizados.
 *
 * Si el indice viene vacio —las categorias no han cargado, o han fallado— se
 * devuelve lo escrito sin tocar. Normalizar contra un vocabulario que no se ha
 * podido leer solo serviria para borrar informacion buena.
 */
export function normalizarFocoClinico(seleccionadas, therapyCats) {
  if (indiceDeFoco(therapyCats).size === 0) {
    return (seleccionadas || [])
      .map((x) => String(x ?? "").trim())
      .filter(Boolean);
  }
  return revisarFocoClinico(seleccionadas, therapyCats).valores;
}
